import { useEffect, useState } from "react";
import {
  buildRule,
  ORDINAL_LABELS,
  parseRule,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type RecurrenceParts,
  type Unit,
} from "@/lib/recurrence";
import { pluralRu } from "@/lib/plural";

interface Props {
  value: string | null;
  onSave: (rule: string) => void;
  onBack: () => void;
  onCancel: () => void;
}

const UNIT_OPTIONS: { value: Unit; forms: readonly [string, string, string] }[] = [
  { value: "DAILY", forms: ["день", "дня", "дней"] },
  { value: "WEEKLY", forms: ["неделя", "недели", "недель"] },
  { value: "MONTHLY", forms: ["месяц", "месяца", "месяцев"] },
  { value: "YEARLY", forms: ["год", "года", "лет"] },
];

const RU_WEEKDAY: Record<string, string> = {
  MO: "Пн", TU: "Вт", WE: "Ср", TH: "Чт", FR: "Пт", SA: "Сб", SU: "Вс",
};

export default function RecurrenceCustomEditor({ value, onSave, onBack, onCancel }: Props) {
  const [parts, setParts] = useState<RecurrenceParts>(() => parseRule(value));

  useEffect(() => {
    setParts(parseRule(value));
  }, [value]);

  function toggleByDay(d: string) {
    setParts((p) => {
      const has = p.byday.includes(d);
      return { ...p, byday: has ? p.byday.filter((x) => x !== d) : [...p.byday, d] };
    });
  }

  return (
    <>
      <div className="px-1 pb-2 flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 mb-2">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer"
          aria-label="Назад к пресетам"
        >
          ← Назад
        </button>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Настроить</span>
      </div>

      <div className="px-1 mb-2 flex items-center gap-2">
        <span className="text-xs text-gray-600 dark:text-gray-300">Каждые</span>
        <input
          type="number"
          min={1}
          value={parts.interval}
          onChange={(e) =>
            setParts((p) => ({ ...p, interval: Math.max(1, parseInt(e.target.value, 10) || 1) }))
          }
          className="w-14 text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 outline-none focus:border-blue-500 text-gray-900 dark:text-gray-100"
        />
        <select
          value={parts.unit}
          onChange={(e) => setParts((p) => ({ ...p, unit: e.target.value as Unit }))}
          className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-gray-700 dark:text-gray-200"
        >
          {UNIT_OPTIONS.map((u) => (
            <option key={u.value} value={u.value}>
              {pluralRu(parts.interval, u.forms)}
            </option>
          ))}
        </select>
      </div>

      {parts.unit === "WEEKLY" && (
        <div className="px-1 mb-2">
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">По дням:</p>
          <div className="flex items-center gap-1">
            {WEEKDAYS.map((d) => {
              const on = parts.byday.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleByDay(d)}
                  aria-pressed={on}
                  aria-label={RU_WEEKDAY[d]}
                  className={`w-6 h-6 rounded-full text-[10px] font-medium flex items-center justify-center cursor-pointer ${
                    on
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {WEEKDAY_LABELS[d]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {parts.unit === "MONTHLY" && (
        <div className="px-1 mb-2 space-y-2">
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
            <input
              type="radio"
              name="monthlyMode"
              checked={parts.monthlyMode === "day"}
              onChange={() => setParts((p) => ({ ...p, monthlyMode: "day" }))}
            />
            <span>В день</span>
            <input
              type="number"
              min={1}
              max={31}
              value={parts.monthDay}
              aria-label="День месяца"
              onChange={(e) =>
                setParts((p) => ({
                  ...p,
                  monthlyMode: "day",
                  monthDay: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)),
                }))
              }
              className="w-14 text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 outline-none focus:border-blue-500 text-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 flex-wrap">
            <input
              type="radio"
              name="monthlyMode"
              checked={parts.monthlyMode === "weekday"}
              onChange={() => setParts((p) => ({ ...p, monthlyMode: "weekday" }))}
            />
            <span>В</span>
            <select
              value={parts.weekdayPos}
              onChange={(e) =>
                setParts((p) => ({
                  ...p,
                  monthlyMode: "weekday",
                  weekdayPos: parseInt(e.target.value, 10),
                }))
              }
              aria-label="Позиция дня недели"
              className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-gray-700 dark:text-gray-200"
            >
              {ORDINAL_LABELS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={parts.weekdayName}
              onChange={(e) =>
                setParts((p) => ({
                  ...p,
                  monthlyMode: "weekday",
                  weekdayName: e.target.value,
                }))
              }
              aria-label="День недели"
              className="text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1 text-gray-700 dark:text-gray-200"
            >
              {WEEKDAYS.map((d) => (
                <option key={d} value={d}>
                  {RU_WEEKDAY[d]}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="px-1 pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer px-2 py-1"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => onSave(buildRule(parts))}
          className="text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md px-3 py-1"
        >
          Сохранить
        </button>
      </div>
    </>
  );
}
